import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Pencil, Trash2, Save, X, Zap, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { projectsAPI } from '../../services/api';

const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try { const res = await projectsAPI.getAll(); setProjects(res.data || []); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const resetForm = () => { setEditing(null); setForm({}); };

  const startNew = () => {
    setEditing('new');
    setForm({ title: '', slug: '', mw: '', location: '', project_type: '', client: '', summary: '', outcome: '', challenges: '', approach: '', thumbnail: '', images: '' });
  };

  const startEdit = (p) => {
    setEditing(p._id);
    setForm({ ...p, mw: String(p.mw || ''), images: (p.images || []).join('\n') });
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) { toast.error('Title and slug are required'); return; }
    const payload = { ...form, mw: Number(form.mw) || 0, images: typeof form.images === 'string' ? form.images.split('\n').filter(Boolean) : form.images };
    delete payload._id;
    try {
      if (editing === 'new') { await projectsAPI.create(payload); toast.success('Created'); }
      else { await projectsAPI.update(editing, payload); toast.success('Updated'); }
      resetForm(); load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await projectsAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{projects.length} projects</p>
        <Button onClick={startNew} data-testid="new-project-btn"><Plus className="w-4 h-4 mr-2" /> New Project</Button>
      </div>

      {editing && (
        <Card className="mb-6 border-emerald-200" data-testid="project-form">
          <CardHeader><CardTitle>{editing === 'new' ? 'New Project' : 'Edit Project'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-sm font-medium mb-1 block">Title *</label><Input value={form.title || ''} onChange={e => setForm({...form, title: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Slug *</label><Input value={form.slug || ''} onChange={e => setForm({...form, slug: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">MW</label><Input type="number" value={form.mw || ''} onChange={e => setForm({...form, mw: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Location</label><Input value={form.location || ''} onChange={e => setForm({...form, location: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Type</label><Input value={form.project_type || ''} onChange={e => setForm({...form, project_type: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Client</label><Input value={form.client || ''} onChange={e => setForm({...form, client: e.target.value})} /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Summary</label><Textarea rows={2} value={form.summary || ''} onChange={e => setForm({...form, summary: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Outcome</label><Textarea rows={2} value={form.outcome || ''} onChange={e => setForm({...form, outcome: e.target.value})} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-sm font-medium mb-1 block">Challenges</label><Textarea rows={2} value={form.challenges || ''} onChange={e => setForm({...form, challenges: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Approach</label><Textarea rows={2} value={form.approach || ''} onChange={e => setForm({...form, approach: e.target.value})} /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Thumbnail URL</label><Input value={form.thumbnail || ''} onChange={e => setForm({...form, thumbnail: e.target.value})} /></div>
            <div><label className="text-sm font-medium mb-1 block">Image URLs (one per line)</label><Textarea rows={3} value={typeof form.images === 'string' ? form.images : (form.images || []).join('\n')} onChange={e => setForm({...form, images: e.target.value})} /></div>
            <div className="flex gap-2">
              <Button onClick={handleSave}><Save className="w-4 h-4 mr-1" /> Save</Button>
              <Button variant="outline" onClick={resetForm}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {projects.map(p => (
          <Card key={p._id}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                {p.thumbnail && <img src={p.thumbnail} alt={p.title} className="w-16 h-12 object-cover rounded" />}
                <div>
                  <h3 className="font-semibold text-gray-900">{p.title}</h3>
                  <div className="flex gap-2 text-xs text-gray-500 mt-0.5">
                    <span className="flex items-center gap-0.5"><Zap className="w-3 h-3" />{p.mw} MW</span>
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.location}</span>
                    <span className="px-1.5 bg-emerald-50 text-emerald-700 rounded">{p.project_type}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(p)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(p._id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProjectsManager;
